const BUDGET_TEAM_LABEL_ALIAS_GROUPS = [
  new Set(['инж', 'инженеры']),
]

function normalizeLabel(value) {
  return String(value || '').trim().toLowerCase()
}

function labelAliasVariants(label) {
  const normalized = normalizeLabel(label)
  if (!normalized) return new Set()
  const variants = new Set([normalized])
  BUDGET_TEAM_LABEL_ALIAS_GROUPS.forEach((group) => {
    if (group.has(normalized)) {
      group.forEach((item) => variants.add(item))
    }
  })
  return variants
}

function teamMatchesLabel(department, team, label) {
  const aliases = labelAliasVariants(label)
  if (!aliases.size) return false
  const departmentName = normalizeLabel(department?.department_name)
  const teamName = normalizeLabel(team?.team_name)
  return [...aliases].some((alias) => (
    alias === departmentName
    || alias === teamName
    || departmentName.startsWith(`${alias} `)
  ))
}

export function collectSubTeamWeightsFromStagesIncome(stagesIncome = [], label) {
  const weights = new Map()

  ;(stagesIncome || []).forEach((stage) => {
    ;(stage?.departments || []).forEach((department) => {
      ;(department?.teams || []).forEach((team) => {
        if (!teamMatchesLabel(department, team, label)) return
        ;(team?.employees || []).forEach((employee) => {
          const subTeam = String(employee?.sub_team || '').trim()
          if (!subTeam) return
          const income = Number(employee?.income || employee?.total_income || 0)
          const weight = income > 0 ? income : 1
          weights.set(subTeam, (weights.get(subTeam) || 0) + weight)
        })
      })
    })
  })

  return [...weights.entries()]
    .map(([sub_team, weight]) => ({ sub_team, weight }))
    .filter((item) => item.weight > 0)
    .sort((a, b) => a.sub_team.localeCompare(b.sub_team, 'ru'))
}

function fixRoundingDrift(parts, targetTotal) {
  if (!parts.length) return
  const drift = Math.round((Number(targetTotal) - parts.reduce((sum, part) => sum + part, 0)) * 100) / 100
  if (drift) {
    parts[parts.length - 1] = Math.round((parts[parts.length - 1] + drift) * 100) / 100
  }
}

export function splitBudgetValuesByWeights(values = {}, weights = []) {
  if (!values || !weights.length) return null

  const totalWeight = weights.reduce((sum, item) => sum + item.weight, 0)
  if (totalWeight <= 0) return null

  const columnIds = Object.keys(values)
  const split = Object.fromEntries(weights.map((item) => [item.sub_team, {}]))

  columnIds.forEach((columnId) => {
    const source = values[columnId] || {}
    const planTotal = Number(source.plan || 0)
    const factTotal = Number(source.fact || 0)
    const percentTotal = Number(source.percent || 0)

    const planParts = []
    const factParts = []
    const percentParts = []

    weights.forEach((item) => {
      const fraction = item.weight / totalWeight
      planParts.push(Math.round(planTotal * fraction * 100) / 100)
      factParts.push(Math.round(factTotal * fraction * 100) / 100)
      percentParts.push(Math.round(percentTotal * fraction * 100) / 100)
    })

    fixRoundingDrift(planParts, planTotal)
    fixRoundingDrift(factParts, factTotal)
    fixRoundingDrift(percentParts, percentTotal)

    weights.forEach((item, index) => {
      split[item.sub_team][columnId] = {
        plan: planParts[index],
        fact: factParts[index],
        percent: percentParts[index],
      }
    })
  })

  return split
}

export function enrichBudgetTableWithSubTeams(table, stagesIncome = []) {
  // Fallback: если API не вернул row_type team-group, раскрываем по stages_income.
  // Веса здесь — по доходам сотрудников на объекте, не по base_percentage оргструктуры.
  if (!table?.rows?.length) return table

  const rows = []
  table.rows.forEach((row) => {
    if (row.row_type === 'team-group' || row.row_type === 'sub-team' || row.is_total) {
      rows.push(row)
      return
    }
    if (row.category !== 'team_income') {
      rows.push(row)
      return
    }

    const weights = collectSubTeamWeightsFromStagesIncome(stagesIncome, row.label)
    if (weights.length < 2) {
      rows.push(row)
      return
    }

    const splitValues = splitBudgetValuesByWeights(row.values, weights)
    if (!splitValues) {
      rows.push(row)
      return
    }

    rows.push({
      ...row,
      row_type: 'team-group',
      expandable: true,
      sub_team_count: weights.length,
    })

    weights.forEach((weightItem) => {
      rows.push({
        id: `${row.id}-sub-${weightItem.sub_team}`,
        parent_id: row.id,
        row_type: 'sub-team',
        section: row.section,
        label: weightItem.sub_team,
        category: row.category,
        values: splitValues[weightItem.sub_team] || {},
      })
    })
  })

  return { ...table, rows }
}
